import { useEffect, useRef } from 'react';
import { useLoopbackRooms } from '../hooks/useLoopbackRooms.js';
import { useStats } from '../hooks/useStats.js';
import { HangupIcon, AlertIcon } from '../icons.js';

export function LoopbackView() {
  const { publisherVideo, mirrorVideo, stop, error } = useLoopbackRooms();
  const stats = useStats(publisherVideo, mirrorVideo);

  const localRef = useRef<HTMLVideoElement>(null);
  const mirrorRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (publisherVideo && localRef.current) publisherVideo.attach(localRef.current);
    return () => { if (publisherVideo && localRef.current) publisherVideo.detach(localRef.current); };
  }, [publisherVideo]);

  useEffect(() => {
    if (mirrorVideo && mirrorRef.current) {
      mirrorVideo.attach(mirrorRef.current);
      mirrorRef.current.play().catch(() => {});
    }
    return () => { if (mirrorVideo && mirrorRef.current) mirrorVideo.detach(mirrorRef.current); };
  }, [mirrorVideo]);

  return (
    <section className="space-y-7 animate-fade-up">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-eyebrow mb-2">Self-test</div>
          <h1 className="h-section text-ink-900 dark:text-ink-100">Camera &amp; mic check</h1>
          <p className="hint mt-2 max-w-prose">
            Your camera is publishing into a private test room. The right tile is what someone
            else would see — proof that capture, encoding and the relay path are all working.
          </p>
        </div>
        <button type="button" className="btn-danger" onClick={() => { void stop(); }}>
          <HangupIcon /> Stop test
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <figure className="video-tile aspect-[16/9]">
          <video ref={localRef} autoPlay muted playsInline />
          <figcaption className="tile-label">Local preview</figcaption>
        </figure>
        <figure className="video-tile aspect-[16/9]">
          <video ref={mirrorRef} autoPlay muted playsInline />
          <figcaption className="tile-label">After relay (what others see)</figcaption>
        </figure>
      </div>

      <div className="stats-bar">
        <div><span className="stats-key">Sent</span> {stats.sentDisplay}</div>
        <div><span className="stats-key">Received</span> {stats.recvDisplay}</div>
        <div><span className="stats-key">Resolution out</span> {stats.sentRes}</div>
        <div><span className="stats-key">Resolution in</span> {stats.recvRes}</div>
        <div><span className="stats-key">Source</span> {stats.source}</div>
      </div>

      {error ? (
        <div className="notice-error">
          <AlertIcon className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      ) : null}
    </section>
  );
}
